import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";

import Sidebar from "./components/Sidebar";
import LockTypes from "./components/LockTypes";
import LockProperties from "./components/LockProperties";
import SupplierManagement from "./components/SupplierManagement";
import TaxChange from "./components/TaxChange";
import DatabasePreview from "./components/DatabasePreview";
import Backups from "./components/Backups";
import DatabaseUpdate from "./components/DatabaseUpdate";
import UserManagement from "./components/UserManagement";
import LoginScreen from "./LoginScreen";
import { isAdminLoggedIn, adminLogout, getAdminPermissions } from "./adminApi";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import "./AdminPanel.css";

export type ViewType =
  | "Lock types"
  | "Lock properties"
  | "Supplier Icon"
  | "Database preview"
  | "Backups"
  | "Tax change"
  | "Database update"
  | "Users";

// 🔹 Development flag
const DEV_MODE = true; // ⬅ set to false for real Microsoft login

const AdminPanelContent: React.FC = () => {
  const { instance, accounts } = useMsal();
  const { lang, setLang, t } = useLanguage();
  const permissions = getAdminPermissions();

  const [activeView, setActiveView] = useState<ViewType>(
    (permissions.includes("Lock types") ? "Lock types" : (permissions[0] as ViewType)) || "Lock types"
  );
  const [devAuthenticated, setDevAuthenticated] = useState(isAdminLoggedIn());
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(isAdminLoggedIn());

  const viewTitles: Record<ViewType, string> = {
    "Lock types": t("Lock types"),
    "Lock properties": t("Lock properties"),
    "Supplier Icon": t("Supplier Icon"),
    "Database preview": t("Database preview"),
    "Backups": t("Backups"),
    "Tax change": t("Tax change"),
    "Database update": t("Database update"),
    "Users": t("Users"),
  };

  // ---- Auth state ----
  const isAuthenticated = DEV_MODE ? devAuthenticated : accounts.length > 0;
  const username = DEV_MODE
    ? "admin"
    : accounts[0]?.username || "";

  const handleLogout = () => {
    if (DEV_MODE) {
      adminLogout();
      setDevAuthenticated(false);
    } else {
      instance.logoutPopup().catch((e) => {
        console.error(e);
      });
    }
  };

  // ---- Render selected view ----
  const renderView = () => {
    if (!permissions.includes(activeView)) {
      return <h2>{t("Brak uprawnień do tego widoku")}</h2>;
    }

    switch (activeView) {
      case "Lock types":
        return <LockTypes />;
      case "Lock properties":
        return <LockProperties />;
      case "Supplier Icon":
        return <SupplierManagement />;
      case "Tax change":
        return <TaxChange />;
      case "Database preview":
        return <DatabasePreview />;
      case "Backups":
        return <Backups />;
      case "Database update":
        return <DatabaseUpdate />;
      case "Users":
        return <UserManagement />;
      default:
        return <h2>{t("Choose view from the menu")}</h2>;
    }
  };

  // ---- Show login screen if not authenticated ----
  if (!isAuthenticated) {
    return (
      <LoginScreen
        DEV_MODE={DEV_MODE}
        onDevLogin={() => setDevAuthenticated(true)}
      />
    );
  }

  // ---- Admin panel ----
  return (
    <div className="app-container">
      {/* Always on top */}
      <div className="topbar">
        <span>{username}</span>
        <span className="topbar-title">{viewTitles[activeView]}</span>
        
        <div className="language-switcher" style={{ marginLeft: 'auto', marginRight: '20px', display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => setLang('en')} 
            style={{ 
              fontWeight: lang === 'en' ? 'bold' : 'normal',
              background: lang === 'en' ? '#f0f0f0' : 'none',
              border: '1px solid #ccc',
              padding: '2px 8px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            EN
          </button>
          <button 
            onClick={() => setLang('de')}
            style={{ 
              fontWeight: lang === 'de' ? 'bold' : 'normal',
              background: lang === 'de' ? '#f0f0f0' : 'none',
              border: '1px solid #ccc',
              padding: '2px 8px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            DE
          </button>
        </div>

        <button onClick={handleLogout}>
          {t("Logout")}
        </button>
      </div>

      {/* Sidebar + Content below topbar */}
      <div className="main-content">
        <Sidebar activeView={activeView} setActiveView={setActiveView} />
        <div className="content">{renderView()}</div>
      </div>
    </div>
  );
};

const AdminPanel: React.FC = () => {
  return (
    <LanguageProvider>
      <AdminPanelContent />
    </LanguageProvider>
  );
};

export default AdminPanel;
